import React, { useState } from 'react';
import { Eye, EyeOff, Lock, User, AlertCircle, Phone } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { Base_Url } from '../config/domainConfig';
import './LoginForm.css';

const LoginForm = () => {
  const { showError, showSuccess } = useToast();
  const [loginType, setLoginType] = useState('admin'); // 'admin' or 'user'
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const API_BASE_URL = `${Base_Url}/api`;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (loginType === 'admin') {
        // Admin login
        const response = await fetch(`${API_BASE_URL}/auth/admin-login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password
          }),
        });

        const data = await response.json();

        if (data.success) {
          setIsAuthenticated(true);
          localStorage.setItem('adminToken', data.data.token);
          localStorage.setItem('adminUser', JSON.stringify(data.data.admin));
          showSuccess('Admin login successful!');
          window.location.href = '/admin';
        } else {
          setError(data.message || 'Invalid admin credentials. Please try again.');
          showError(data.message || 'Invalid admin credentials. Please try again.');
        }
      } else {
        // User login with phone and password
        const response = await fetch(`${API_BASE_URL}/auth/user-login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            phone: formData.phone,
            password: formData.password
          }),
        });

        const data = await response.json();

        if (data.success) {
          setIsAuthenticated(true);
          localStorage.setItem('userToken', data.data.token);
          localStorage.setItem('userData', JSON.stringify(data.data.user));
          showSuccess('User login successful!');
          window.location.href = '/user';
        } else {
          setError(data.message || 'Invalid phone or password. Please try again.');
          showError(data.message || 'Invalid phone or password. Please try again.');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed. Please try again.');
      showError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <span className="logo-text">Vikas Fund</span>
          </div>
          <h1>{loginType === 'admin' ? 'Admin Login' : 'Applicant Login'}</h1>
          <p>{loginType === 'admin' ? 'Enter your credentials to access the admin panel' : 'Enter your phone and password to access your applications'}</p>
        </div>

        {/* Login Type Toggle */}
        <div className="login-toggle">
          <button
            type="button"
            className={`toggle-btn ${loginType === 'admin' ? 'active' : ''}`}
            onClick={() => setLoginType('admin')}
          >
            Admin Login
          </button>
          <button
            type="button"
            className={`toggle-btn ${loginType === 'user' ? 'active' : ''}`}
            onClick={() => setLoginType('user')}
          >
            Applicant Login
          </button>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="error-message">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {loginType === 'admin' ? (
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-wrapper">
                <User size={18} className="input-icon" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  required
                  className={error ? 'error' : ''}
                />
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <div className="input-wrapper">
                <Phone size={18} className="input-icon" />
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter your phone number"
                  required
                  className={error ? 'error' : ''}
                />
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <Lock size={18} className="input-icon" />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                required
                className={error ? 'error' : ''}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="form-options">
            <label className="checkbox-wrapper">
              <input type="checkbox" />
              <span className="checkmark"></span>
              Remember me
            </label>
            <a href="#" className="forgot-password">Forgot Password?</a>
          </div>

          <button
            type="submit"
            className={`login-button ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="spinner"></div>
                Signing In...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <div className="login-footer">
          <p>Demo Credentials:</p>
          {loginType === 'admin' ? (
            <p><strong>Admin:</strong> admin@vikasfund.com / admin123</p>
          ) : (
            <p><strong>Applicant:</strong> Use your phone number as both login ID and password</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginForm; 